import { SalesCallbackBanner } from "@/components/SalesCallbackBanner";
import { useState, useRef, useEffect } from "react";
import { Link, useLocation, Redirect } from "wouter";
import {
  LayoutDashboard, ShoppingBag, Wallet, ShieldAlert, FileText,
  Building2, Users, Megaphone, Settings, Sparkles, Bot,
  ChevronLeft, ChevronRight, Menu, X, LogOut,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface NavPage {
  href: string;
  label: string;
  adminOnly: boolean;
  modVisible: boolean;
  journalistVisible: boolean;
}

interface NavGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  pages: NavPage[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    pages: [
      { href: "/admin", label: "Overview", adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "orders",
    label: "Orders",
    icon: ShoppingBag,
    pages: [
      { href: "/admin/hub-dash",           label: "Hub Dash",          adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/orders",             label: "All Orders",        adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/multi-orders",       label: "Multi-Store",       adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/live-orders",        label: "Live",              adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/wallboard",          label: "Wallboard",         adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/guest-activity",     label: "Checkout Recovery", adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/order-stores",       label: "Shops",             adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/sales",              label: "Sales & Onboarding", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/drivers",            label: "Drivers",           adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/cash-returns", label: "Driver Cash Returns", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/delivery-runs",      label: "Delivery Runs",     adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/dispatch-zones",     label: "Dispatch Zones",    adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/delivery-disputes",  label: "Disputes",          adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/boosts",             label: "Store Boosts",      adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/receipt-design",     label: "Receipt Design",    adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/store/scan",               label: "Scanner",           adminOnly: true,  modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "finance",
    label: "Finance",
    icon: Wallet,
    pages: [
      { href: "/admin/analytics",       label: "Analytics",       adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/reconciliation",  label: "Reconciliation",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/seo", label: "Search visibility", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/hub-revenue",     label: "Hub Finance",     adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/agency-compliance", label: "Agreements & Compliance", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/transactions",    label: "Transactions",    adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/cash-fees",      label: "Cash Fees",       adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/payment-log", label: "Payment Log", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/cashbook",       label: "Cashbook",        adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/global-codes",   label: "Global Codes",    adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/influencers",    label: "Influencers",     adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/feed-ads",       label: "Feed Adverts",    adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/bank-reconciliation", label: "Bank Reconciliation", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/driver-wallets", label: "Driver Wallets",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/driver-payouts", label: "Driver Payouts",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/tips",           label: "Tips",            adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/pricing",        label: "Pricing & Stripe",adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/sponsor",        label: "Sponsor",         adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/competitions",   label: "Competitions",    adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "moderation",
    label: "Moderation",
    icon: ShieldAlert,
    pages: [
      { href: "/admin/pending",    label: "Pending",    adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/reports",    label: "Reports",    adminOnly: false, modVisible: true,  journalistVisible: false },
      { href: "/admin/support",    label: "Support",    adminOnly: false, modVisible: true,  journalistVisible: false },
      { href: "/admin/office-phones", label: "Office phones", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/printer-health", label: "Printer Health", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/remote-support", label: "Remote Support", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/messages",   label: "Messages",   adminOnly: false, modVisible: true,  journalistVisible: false },
      { href: "/admin/free-items", label: "Market",     adminOnly: false, modVisible: true,  journalistVisible: false },
      { href: "/admin/reviews",    label: "Reviews",    adminOnly: true,  modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "content",
    label: "Content",
    icon: FileText,
    pages: [
      { href: "/admin/news",             label: "News",        adminOnly: false, modVisible: false, journalistVisible: true  },
      { href: "/admin/events",           label: "Events",      adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/deals",            label: "Deals",       adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/deal-redemptions", label: "Redemptions", adminOnly: true,  modVisible: false, journalistVisible: false },
      { href: "/admin/content/jobs",     label: "Jobs",        adminOnly: true,  modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "directory",
    label: "Directory",
    icon: Building2,
    pages: [
      { href: "/admin/businesses", label: "Businesses",   adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/venues",     label: "Venues",       adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/venue-review", label: "Venue Review", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/definitions",  label: "Facilities",   adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/trades",     label: "Tradespeople", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/categories", label: "Categories",   adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/towns",      label: "Towns",        adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "users",
    label: "Users",
    icon: Users,
    pages: [
      { href: "/admin/users",     label: "All Users",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/audit-log", label: "Audit Log",  adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: Megaphone,
    pages: [
      { href: "/admin/campaigns",       label: "Email Campaigns", adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/push",            label: "Push",            adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/sms",             label: "SMS",             adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/website-briefs",  label: "Website Briefs",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/facebook",        label: "Facebook",        adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/kindness",        label: "Kindness",        adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: Settings,
    pages: [
      { href: "/admin/integrations", label: "Integrations", adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "fun",
    label: "Fun",
    icon: Sparkles,
    pages: [
      { href: "/admin/quiz",         label: "Quiz Events",  adminOnly: true, modVisible: false, journalistVisible: false },
      { href: "/admin/quiz/history", label: "Quiz History", adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
  {
    key: "ai",
    label: "AI",
    icon: Bot,
    pages: [
      { href: "/admin/claude", label: "Claude AI", adminOnly: true, modVisible: false, journalistVisible: false },
    ],
  },
];

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-slate-500">Loading…</span>
      </div>
    </div>
  );
}

export function AdminLayout({ children, current }: { children: React.ReactNode; current?: string }) {
  const [location] = useLocation();
  const { user, isLoading, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const subNavRef = useRef<HTMLDivElement>(null);

  const activePath = current || location;

  // Scroll active sub-tab into view. MUST stay above the early returns below so the
  // hook order is identical on every render — otherwise React throws #310 ("rendered
  // more hooks than during the previous render") and white-screens the whole admin.
  useEffect(() => {
    const el = subNavRef.current?.querySelector("[data-active='true']") as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [activePath]);

  // Auth guard — prevents white page flash
  if (isLoading) return <LoadingSkeleton />;

  const isAdmin = user?.role === "admin";
  const isMod = isAdmin || (user as { isModerator?: boolean } | null)?.isModerator === true;
  const isJournalist = isAdmin || (user as { isJournalist?: boolean } | null)?.isJournalist === true;
  const hasAccess = isAdmin || isMod || isJournalist;

  if (!hasAccess) return <Redirect to="/" />;

  function filterPages(pages: NavPage[]) {
    return pages.filter(({ adminOnly, modVisible, journalistVisible }) => {
      if (isAdmin) return true;
      if (modVisible && isMod) return true;
      if (journalistVisible && isJournalist) return true;
      return !adminOnly && !modVisible && !journalistVisible;
    });
  }

  const visibleGroups = NAV_GROUPS.map(g => ({ ...g, pages: filterPages(g.pages) })).filter(g => g.pages.length > 0);

  // Determine active group from current path
  const activeGroup = visibleGroups.find(g =>
    g.pages.some(p => {
      if (p.href === "/admin") return activePath === "/admin";
      return activePath === p.href || activePath.startsWith(p.href + "/");
    })
  ) ?? visibleGroups[0];

  const activeGroupPages = activeGroup?.pages ?? [];
  const isDashboard = activeGroup?.key === "dashboard";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* ── Top bar ── */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 shadow-md">
        <div className="flex items-center h-14 px-4 gap-3">
          {/* Logo / brand */}
          <Link href="/admin" className="flex items-center gap-2 shrink-0 mr-2">
            <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center text-white font-black text-xs">AH</div>
            <span className="font-semibold text-sm hidden sm:block text-slate-100">Admin</span>
          </Link>

          {/* Category nav — desktop */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 overflow-x-auto scrollbar-none">
            {visibleGroups.map(g => {
              const Icon = g.icon;
              const isActive = g.key === activeGroup?.key;
              return (
                <Link
                  key={g.key}
                  href={g.pages[0]?.href ?? "/admin"}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-slate-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <Icon size={14} />
                  {g.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 ml-auto shrink-0">
            {/* User info */}
            <span className="text-xs text-slate-400 hidden sm:block">
              {user?.name ?? user?.email ?? "Admin"}
            </span>
            <button
              onClick={() => void logout()}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-white/10"
              title="Sign out"
            >
              <LogOut size={14} />
              <span className="hidden sm:block">Sign out</span>
            </button>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1.5 rounded hover:bg-white/10 text-slate-300"
              onClick={() => setMobileOpen(o => !o)}
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* ── Sub-nav (desktop) — only when group has multiple pages ── */}
        {!isDashboard && activeGroupPages.length > 1 && (
          <div
            ref={subNavRef}
            className="hidden md:flex items-center gap-0.5 px-4 pb-2 overflow-x-auto scrollbar-none border-t border-white/10"
          >
            {activeGroupPages.map(page => {
              const isActive = activePath === page.href || (page.href !== "/admin" && activePath.startsWith(page.href + "/"));
              return (
                <Link
                  key={page.href}
                  href={page.href}
                  data-active={isActive ? "true" : undefined}
                  className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-slate-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {page.label}
                </Link>
              );
            })}
          </div>
        )}
      </header>

      {/* ── Mobile nav drawer ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setMobileOpen(false)}>
          <nav
            className="absolute top-14 left-0 right-0 bg-slate-900 text-white overflow-y-auto max-h-[calc(100vh-56px)]"
            onClick={e => e.stopPropagation()}
          >
            {visibleGroups.map(g => {
              const Icon = g.icon;
              const isActiveGroup = g.key === activeGroup?.key;
              return (
                <div key={g.key} className="border-b border-white/10">
                  <div className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-widest ${isActiveGroup ? "text-primary" : "text-slate-400"}`}>
                    <Icon size={13} />
                    {g.label}
                  </div>
                  <div className="flex flex-col pb-2">
                    {g.pages.map(page => {
                      const isActive = activePath === page.href || (page.href !== "/admin" && activePath.startsWith(page.href + "/"));
                      return (
                        <Link
                          key={page.href}
                          href={page.href}
                          onClick={() => setMobileOpen(false)}
                          className={`px-6 py-2 text-sm font-medium transition-colors ${
                            isActive
                              ? "text-white bg-primary/80"
                              : "text-slate-300 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          {page.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      )}

      {/* ── Sub-nav (mobile) — horizontal scroll strip ── */}
      {!isDashboard && activeGroupPages.length > 1 && (
        <div className="md:hidden bg-slate-800 sticky top-14 z-20 flex items-center gap-1 px-3 py-2 overflow-x-auto scrollbar-none border-b border-slate-700">
          {activeGroupPages.map(page => {
            const isActive = activePath === page.href || (page.href !== "/admin" && activePath.startsWith(page.href + "/"));
            return (
              <Link
                key={page.href}
                href={page.href}
                className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors shrink-0 ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-slate-300 bg-slate-700 hover:bg-slate-600"
                }`}
              >
                {page.label}
              </Link>
            );
          })}
        </div>
      )}

      {/* ── Breadcrumb strip ── */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 text-xs text-slate-500">
        {activeGroup && (
          <>
            <activeGroup.icon size={12} className="text-slate-400" />
            <span>{activeGroup.label}</span>
            {!isDashboard && (
              <>
                <ChevronRight size={12} className="text-slate-300" />
                <span className="font-medium text-slate-700">
                  {activeGroupPages.find(p =>
                    activePath === p.href || (p.href !== "/admin" && activePath.startsWith(p.href + "/"))
                  )?.label ?? ""}
                </span>
              </>
            )}
          </>
        )}
        {/* Back to site */}
        <Link href="/" className="ml-auto flex items-center gap-1 text-slate-400 hover:text-slate-700 transition-colors">
          <ChevronLeft size={12} />
          Back to site
        </Link>
      </div>

      {/* ── Page content ── */}
      <main className="flex-1 min-w-0 container mx-auto px-4 py-6 max-w-screen-2xl">
        {isAdmin && <SalesCallbackBanner />}
        {children}
      </main>
    </div>
  );
}
